import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Divider, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import { Button } from "rsuite";
import { useLocation, useNavigate } from "react-router-dom";
import deliveredIcon from "../../assets/icons/deliveredIcon.svg";
import cancelledIcon from "../../assets/icons/cancelledIcon.svg";
import inProcessIcon from "../../assets/icons/processingIcon.svg";
import StyledOrderDetails from "./StyledOrderDetails";
import { ROUTE_PATH } from "../../constants/route";
import { toTwoDecimalPlaces } from "../../utils/commonFuntions";

// Helper function to format variant options as "Fabric: Velvet / Size: M / Color: Red"
const getVariantOptionsDisplay = (item) => {
  if (!item) return null;
  if (item?.variant_details?.options?.Title === "Default Title") {
    return null;
  }
  // Check variant_details first (new structure)
  if (item?.variant_details?.options) {
    const options = item?.variant_details?.options;
    // Format as "OptionType: Value / OptionType: Value"
    const parts = Object?.entries(options || {})?.map(([key, value]) => {
      if (value) {
        return `${key}: ${value}`;
      }
      return null;
    }).filter(Boolean);
    return parts?.length > 0 ? parts?.join(' / ') : null;
  }
  
  // Check variant_details option_values_with_hex (alternative structure)
  if (item?.variant_details?.option_values_with_hex) {
    const optionValues = item?.variant_details?.option_values_with_hex;
    const parts = Object?.entries(optionValues || {})?.map(([key, option]) => {
      // Handle both object format {value: "...", hex_code: "..."} and direct value
      let value = null;
      if (typeof option === 'object' && option !== null) {
        value = option?.value || option;
      } else {
        value = option;
      }
      if (value) {
        return `${key}: ${value}`;
      }
      return null;
    }).filter(Boolean);
    return parts?.length > 0 ? parts?.join(' / ') : null;
  }
  
  // Fallback to old structure: show color and size if available
  const parts = [];
  if (item?.color) parts.push(`Color: ${item?.color}`);
  if (item?.size) parts.push(`Size: ${item?.size}`);
  return parts?.length > 0 ? parts?.join(' / ') : null;
};

// Helper function to get variant image - handles both array and string cases
const getVariantImage = (item) => {
  if (!item) return null;
  
  // Check variant_details first (new structure)
  if (item?.variant_details?.variant_img) {
    const variantImg = item?.variant_details?.variant_img;
    // Handle both array and string
    if (Array.isArray(variantImg)) {
      return variantImg[0] || null;
    }
    return typeof variantImg === 'string' ? variantImg : null;
  }
  
  // Fallback to product_image
  if (item?.product_image) {
    if (Array.isArray(item.product_image)) {
      return item.product_image[0] || null;
    }
    return typeof item.product_image === 'string' ? item.product_image : null;
  }
  
  return null;
};

const OrderHistoryDistails = () => {
  const location = useLocation();
  const state = location.state;
  const navigate = useNavigate();
  const user = useSelector((state) => state.home.user?.data[0]);

  const statusIcon = (status) => {
    const iconStatus = status?.toLowerCase();

    switch (iconStatus) {
      case "in_transit_to_hotel":
      case "processing":
      case "pending":
        return inProcessIcon;
      case "cancelled":
        return cancelledIcon;
      default:
        return deliveredIcon;
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  return (
    <StyledOrderDetails>
      <Box className='order-history-details-container-wrap'>
        <Box className='order-history-details-header'>
          <ArrowBackIcon
            sx={{ fontSize: "20px", cursor: "pointer" }}
            onClick={() => navigate(ROUTE_PATH.ORDER_HISTORY)}
          />
          <Typography className='order-history-details-title'>
            {`Order Id ${state?.orderData?.order_number}`}
          </Typography>
        </Box>
        <Box className='order-history-details-first-part'>
          <Typography className='order-date'>
            Ordered on <span>{formatDate(state?.orderData?.created_at)}</span>
          </Typography>
          <Typography className='order-date'>
            {state?.orderData?.order_items?.length} piece(s)
          </Typography>
        </Box>
        <Box
          className='order-history-details-status'
          sx={{
            backgroundColor:
              state?.orderData?.status === "PROCESSING" ||
              state?.orderData?.status === "IN_TRANSIT_TO_HOTEL" ||
              state?.orderData?.status === "PENDING"
                ? "#FFF7E5"
                : state?.orderData?.status === "CANCELLED"
                  ? "#F5F5F5"
                  : "#1365381A",
          }}
        >
          <img
            src={statusIcon(state?.orderData?.status)}
            alt='status'
            style={{ width: "20px" }}
          />
          <Typography className='order-status-date'>
            {state?.orderData?.status === "PROCESSING" ||
            state?.orderData?.status === "IN_TRANSIT_TO_HOTEL" ||
            state?.orderData?.status === "PENDING" ? (
              <Typography
                className='order-status-text'
                sx={{ color: "#9C5D00" }}
              >
                Delivery on{" "}
              </Typography>
            ) : state?.orderData?.status === "CANCELLED" ? (
              <Typography
                className='order-status-text'
                sx={{ color: "#656565" }}
              >
                Cancelled as per your request{" "}
              </Typography>
            ) : (
              <Typography
                className='order-status-text'
                sx={{ color: "#136538" }}
              >
                Delivered on{" "}
              </Typography>
            )}
            {state?.orderData?.status === "CANCELLED"
              ? ""
              : formatDate(state?.orderData?.shipping_address?.arrival_date)}
          </Typography>
        </Box>
        {state?.orderData?.order_items.map((element, index) => {
          return (
            <Box className='order-history-details-second-part' key={index}>
              <Box
                className='order-history-details-second-part-product'
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  const productSlug = element?.product_name?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
                  navigate(
                    ROUTE_PATH.PRODUCT_DETAIL?.replace(":product", productSlug)?.replace(":id", element?.id)
                  );
                }}
              >
                <img
                  src={getVariantImage(element) || (Array.isArray(element?.product_image) ? element?.product_image[0] : element?.product_image)}
                  alt=''
                  className='product-image'
                />
                <Box className='order-history-card-body-product-details'>
                  <Typography
                    className='order-name'
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      const productSlug = element?.product_name?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
                      navigate(
                        ROUTE_PATH.PRODUCT_DETAIL?.replace(":product", productSlug)?.replace(":id", element?.id)
                      );
                    }}
                  >
                    {element?.product_brand}
                  </Typography>
                  <Typography className='product-name'>
                    {element?.product_name}
                  </Typography>
                  <Box className='order-history-card-body-product-details-size'>
                    {getVariantOptionsDisplay(element) ? (
                      <Box className='order-history-size'>
                        {getVariantOptionsDisplay(element)}
                      </Box>
                    ) : element?.size && (
                      <Box className='order-history-size'>
                        Size: {element?.size}
                      </Box>
                    )}
                    <Box className='order-history-size'>
                      Qty: {element?.quantity}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className='order-history-details-second-part-price'>{`$${element?.total_price}`}</Box>
            </Box>
          );
        })}
        <Box className='order-history-details-account-deatils'>
          <Typography className='account-deatils-title'>
            Account details
          </Typography>
          <Box className='account-deatils-subtotal'>
            <Typography className='account-deatils-subtitle'>
              Reservation fee
            </Typography>
            <Typography className='account-deatils-amount'>{`$${state?.orderData?.service_fee}`}</Typography>
          </Box>
          <Box className='account-deatils-subtotal'>
            <Typography className='account-deatils-subtitle'>
              Subtotal
            </Typography>
            <Typography className='account-deatils-amount'>{`$${state?.orderData?.order_items?.reduce((sum, element) => sum + parseFloat(element?.total_price), 0)?.toFixed(2)}`}</Typography>
          </Box>
          {state?.orderData?.payment_component?.length > 0
            ? state?.orderData?.payment_component.map((element) => {
                return (
                  <Box className='account-deatils-subtotal'>
                    <Typography className='account-deatils-subtitle'>
                      {element?.name}
                    </Typography>
                    <Typography className='account-deatils-amount'>{`$${toTwoDecimalPlaces(element?.applied_value)}`}</Typography>
                  </Box>
                );
              })
            : null}
          {/* <Box className='account-deatils-subtotal'>
            <Typography className='account-deatils-subtitle'>
              Subtotal
            </Typography>
            <Typography className='account-deatils-amount'>{`$${state?.orderData?.subtotal}`}</Typography>
          </Box> */}
          <Divider />
          <Box className='account-deatils-total'>
            <Typography className='account-deatils-total-subtitle'>
              Total
            </Typography>
            <Typography className='account-deatils-total-amount'>
              {/* {`$${
             toTwoDecimalPlaces(parseFloat(state?.orderData?.total) +
              (state?.orderData?.payment_component?.length > 0
                ? state?.orderData?.payment_component.reduce(
                    (sum, element) =>
                      sum +
                      (element?.component_type === "percentage"
                        ? calculatePercentage(
                            element?.component_value,
                            state?.orderData?.subtotal
                          )
                        : parseFloat(element?.component_value || 0)),
                    0
                  )
                : 0))
            }`} */}
            {state?.orderData?.total}
            </Typography>
          </Box>
        </Box>
        <Divider
          sx={{
            margin: "20px 0px",
            borderWidth: "3px",
            backgroundColor: "#F0F0F0",
          }}
        />
        <Box className='shipping-details'>
          <Typography className='shipping-title'>Shipping Details</Typography>
          <Box className='shipping-details-box'>
            <PersonOutlineOutlinedIcon
              sx={{ color: "#A78D78", width: "20px" }}
            />
            <Typography className='shipping-details-text'>
              {/* {user?.username} */}
              {state?.orderData?.shipping_address?.delivered_to === null
                ? user?.username
                : state?.orderData?.shipping_address?.delivered_to}
            </Typography>
          </Box>
          <Box
            className='shipping-details-box'
            style={{ alignItems: "flex-start", marginTop: "5px" }}
          >
            <BusinessIcon sx={{ color: "#A78D78", width: "20px" }} />
            <Box>
              <Typography className='shipping-details-text'>
                {state?.orderData?.shipping_address?.hotel}
              </Typography>
              <Typography className='shipping-details-text-2'>
                {state?.orderData?.shipping_address?.hotel_address}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ margin: "20px 0px" }} />
        <Box className='btns-wrap'>
          <Button
            variant='outlined'
            className='continue-shopping-btn'
            onClick={() => navigate(ROUTE_PATH.BROWSE)}
          >
            Continue Shopping
          </Button>
          <Button
            variant='outlined'
            className='continue-shopping-btn help-btn'
            onClick={() => navigate(ROUTE_PATH.CUSTOMER_SUPPORT)}
          >
            <HeadsetMicOutlinedIcon sx={{ width: "11px", height: "11px" }} />
            Need help?
          </Button>
        </Box>
      </Box>
    </StyledOrderDetails>
  );
};

export default OrderHistoryDistails;
